import React from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, Text, Link, VStack, Box, Divider, useColorModeValue
} from '@chakra-ui/react';
import { getTranslations } from '../translations';
import { decode } from '../utils/obfuscate';

const AboutModal = ({ isOpen, onClose }) => {
    const t = getTranslations();
    // Debug: verifica che la traduzione esista
    if (typeof window !== 'undefined' && !t.usageGuideRestTimeVariable) {
        console.warn('usageGuideRestTimeVariable non trovata nelle traduzioni');
    }
    const currentYear = new Date().getFullYear();
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const labelColor = useColorModeValue('teal.600', 'teal.300');
    
    // Testi offuscati (base64 di reverse del testo originale)
    const copyrightText = decode('dGVuLm9mZmFyZ29sYiB9cmFleXstMzIwMiDCqQ==');
    const linkUrl = decode('dGVuLm9mZmFyZ29sYi8vOnNwdHRo');
    const visitText = decode('dGVuLm9mZmFyZ29sYiB0aXNpVg==');

    // Helper function per formattare le voci della guida
    const formatGuideItem = (text) => {
        // Cerca il primo due punti (standard o Unicode) o il primo punto
        const colonIndex = text.indexOf(':');
        const unicodeColonIndex = text.indexOf('：');
        const separatorIndex = colonIndex !== -1 ? colonIndex : (unicodeColonIndex !== -1 ? unicodeColonIndex : -1);
        
        if (separatorIndex === -1) {
            // Se non c'è separatore, mostra tutto il testo normale
            return <Text as="span">{text}</Text>;
        }
        
        const label = text.substring(0, separatorIndex);
        const description = text.substring(separatorIndex + 1).trim();
        
        return (
            <>
                <Text as="span" fontWeight="bold" color={labelColor}>
                    {label}:
                </Text>
                <Text as="span"> {description}</Text>
            </>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered motionPreset="slideInBottom">
            <ModalOverlay backdropFilter="blur(2px)" />
            <ModalContent borderRadius="xl" mx={4} bg={bgColor} borderColor={borderColor}>
                <ModalHeader>{t.aboutTitle}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="start">
                        <Box>
                            <Text fontWeight="bold" mb={1}>{t.version}</Text>
                            <Text fontSize="sm" color="gray.600">
                                {t.aboutText}
                            </Text>
                        </Box>

                        <Divider />

                        <Box>
                            <Text fontWeight="bold" mb={3} fontSize="md">{t.usageGuideTitle || t.usageTitle}</Text>
                            <VStack spacing={3} align="start">
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideWorkTime)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideRestTime)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideRounds)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(
                                            t.usageGuideRestTimeVariable || 
                                            'Riposo Variabile: Permette di configurare tempi di riposo diversi per round specifici. Ad esempio, dopo 4 round riposa per 40 secondi, oppure ogni 3 round riposa per 30 secondi. Utile per allenamenti progressivi dove il tempo di recupero deve aumentare man mano che la fatica si accumula.'
                                        )}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideIcons)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideIconText)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideSave)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideDelete)}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={textColor} lineHeight="1.6">
                                        {formatGuideItem(t.usageGuideFullscreen)}
                                    </Text>
                                </Box>
                            </VStack>
                        </Box>

                        <Divider />

                        <Box w="100%" textAlign="center" pt={2}>
                            <Text fontSize="xs" color="gray.500">
                                {copyrightText.replace('{year}', currentYear)}
                            </Text>
                            <Link href={linkUrl} isExternal color="teal.500" fontSize="sm" fontWeight="bold">
                                {visitText}
                            </Link>
                        </Box>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button colorScheme="teal" onClick={onClose}>
                        OK
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AboutModal;
